import React from 'react';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import _ from 'lodash';
import { css } from 'emotion';
import { Tooltip } from 'antd';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import ProcessDataTable from 'components/core/ProcessDataTable';
import RIEInput from 'components/core/editable/RIEInput';
import RIEUnits from 'components/core/editable/RIEUnits';
import RIEToggle from 'components/core/editable/RIEToggle';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import RIESecondsInput from 'components/core/editable/RIESecondsInput';
import EntityHeader from 'components/core/EntityHeader';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import OverviewViewDropdown from 'components/core/OverviewViewDropdown';
import InputDatasetNameField from 'components/core/fields/InputDatasetNameField';
import OutputVariableMappingField from 'components/core/fields/OutputVariableMappingField';
import VariableDimensionsField from 'components/core/fields/VariableDimensionsField';
import CoordinateSystemField from 'components/core/fields/CoordinateSystemField';
import RetrievedVariableTransformation from 'components/core/fields/transformation_parameters/RetrievedVariableTransformation';
import MappingsTable from 'components/page/process/variableRetrieval/inputs/MappingsTable';
import InfoIcon from 'components/core/InfoIcon';
import InfoMessages from 'components/core/help/InfoMessages';
import { colorTextGrey, colorBlack, colorVeryLightBlue } from 'styles/base';

const emptyStyles = css`
                color: ${colorTextGrey};
                text-align: center;
                font-style: italic;
                padding: 5px 0;
                width: 100%;
                display: inline-block;
            `;

const mappingTableStyles = css`
    table {
        margin-bottom: 0;
    }
    th { 
        background-color: #dbecfa;
        color: ${colorBlack};
    }
    .datastream-priority {
        font-weight: normal;
        color: ${colorTextGrey};
        padding-left: 4px;
    }
`;

const datastreamsListStyles = css`
    color: ${colorTextGrey};
    paddingLeft: 3px;
`;

const transformationTableStyles = css`
    width:100%; 
    .table {
        margin-bottom:0;
        th {
            color: ${colorBlack};
            background-color: ${colorVeryLightBlue};
        }
    }
`;


export default class RetrievedVariablesTable extends ProcessDataTable {
    static propTypes = {
        mode: PropTypes.string,
        onEditRetrievedVariables: PropTypes.func,
        onDeleteRetrievedVariables: PropTypes.func
    };

    _initializePropDependentVariables(props) {
        super._initializePropDependentVariables(props);
        this.coordinateSystemOptions = ProcessUtils.getCoordinateSystemOptionsForVariable(props.process);

        const outputDatastreamOptions = _.union(props.dods, props.process.output_datastreams);
        this.outputDatastreamOptions = Constants.convertArrayToSelectOptions(outputDatastreamOptions);

        this.retrievedVariableCheckUnique = {
            label: 'retrieved variable',
            list: Object.keys(props.process.variable_retrieval.retrieved_variables)
        };
    }

    getRowKey() {
        return 'key';
    }

    getTableRowData(props) {
        const data = [];
        Object.values(props.process.variable_retrieval.retrieved_variables).forEach(v => {
            data.push({
                key: v.name,
                retrievedVariable: v
            })
        });

        return data;
    }

    getRetrievedVariablePath(retrievedVariableName) {
        return ['variable_retrieval', 'retrieved_variables', retrievedVariableName];
    }

    getInputDatasetPath(inputDatasetName) {
        return ['variable_retrieval', 'input_datasets', inputDatasetName];
    }

    getStartOffsetFieldPath(inputDatasetName) {
        return [...this.getInputDatasetPath(inputDatasetName), 'offsets', 0];
    }

    getEndOffsetFieldPath(inputDatasetName) {
        return [...this.getInputDatasetPath(inputDatasetName), 'offsets', 1];
    }

    onRetrievedVariableNameChange = (fieldPath, value, onChangeData) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const oldName = onChangeData.name;
        editor.renameRetrievedVariable(oldName, value, false);
        editor.commit();
    };

    onOutputDatastreamChange = (fieldPath, value, onChangeData) => {
        const variableName = onChangeData.retrievedVariable;
        const outputDatastreams = value.map(valueObj => {
            return valueObj.value;
        });
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.setVariableOutputDatastreams(variableName, outputDatastreams);
        editor.commit();
    };

    onFieldChange = (fieldPath, value) => {
        ProcessUtils.updateField(this.props.process, fieldPath, value);
    };

    onRadioFieldChange = (fieldPath, value) => {
        let saveValue = 0;
        if (value) {
            saveValue = 1;
        }
        ProcessUtils.updateField(this.props.process, fieldPath, saveValue);
    };

    renderRetrievedVariable = (retrievedVariable) => {
        const baseFieldPath = this.getRetrievedVariablePath(retrievedVariable.name);

        return (
            <RIEInput
                value={retrievedVariable.name}
                change={this.onRetrievedVariableNameChange}
                onChangeData={{ name: retrievedVariable.name }}
                fieldPath={[...baseFieldPath, 'name']}
                isDisabled={!this.props.hasWritePermissions}
                urlCommit
                checkUnique={this.retrievedVariableCheckUnique}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
                minWidth="250px"
            />
        );
    };

    renderDataType = (retrievedVariable) => {
        const baseFieldPath = this.getRetrievedVariablePath(retrievedVariable.name);
        const value = Constants.getOptionById(retrievedVariable.data_type, Constants.DATA_TYPES);

        return (
            <RIEComboBox
                value={value}
                options={Constants.DATA_TYPES}
                change={this.onFieldChange}
                fieldPath={[...baseFieldPath, 'data_type']}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
            />
        );
    };

    renderUnits = (retrievedVariable) => {
        const baseFieldPath = this.getRetrievedVariablePath(retrievedVariable.name);
        const value = retrievedVariable.units;

        return (
            <RIEUnits
                value={Constants.convertValueToSelectValue(value)}
                change={this.onFieldChange}
                fieldPath={[...baseFieldPath, 'units']}
                clearable={false}
                options = {Constants.RETRIEVED_VARIABLE_UNITS}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                minWidth="250px"
            />
        );
    };

    renderVariableRequired = (retrievedVariable) => {
        const baseFieldPath = this.getRetrievedVariablePath(retrievedVariable.name);
        const value = retrievedVariable.variable_required;

        return (
            <RIEToggle
                value={value}
                textTrue="Yes"
                textFalse="No"
                fieldPath={[...baseFieldPath, 'variable_required']}
                change={this.onRadioFieldChange}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                radioGroupName={`${retrievedVariable}_variable_required`}
                normalComponentTextOnly
            />
        );
    };

    renderQC = (retrievedVariable) => {
        const baseFieldPath = this.getRetrievedVariablePath(retrievedVariable.name);
        const value = retrievedVariable.retrieve_qc_variable;

        return (
            <RIEToggle
                value={value}
                textTrue="Yes"
                textFalse="No"
                fieldPath={[...baseFieldPath, 'retrieve_qc_variable']}
                change={this.onRadioFieldChange}
                isDisabled={!this.props.hasWritePermissions}
                radioGroupName={`${retrievedVariable}_retrieve_qc_variable`}
                processId={this.props.process.name}
                normalComponentTextOnly
            />
        );
    };

    renderQCRequired = (retrievedVariable) => {
        const qcValue = retrievedVariable.retrieve_qc_variable;

        if (!qcValue) {
            return (<span className={emptyStyles}>QC not retrieved.</span>);
        }

        const baseFieldPath = this.getRetrievedVariablePath(retrievedVariable.name);
        const value = retrievedVariable.qc_variable_required;

        return (
            <RIEToggle
                value={value}
                textTrue="Yes"
                textFalse="No"
                fieldPath={[...baseFieldPath, 'qc_variable_required']}
                change={this.onRadioFieldChange}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                radioGroupName={`${retrievedVariable.name}_qc_variable_required`}
                normalComponentTextOnly
            />
        );
    };

    renderCoordinateSystem = (retrievedVariable) => {
        return (
            <CoordinateSystemField
                process={this.props.process}
                processErrors={this.props.processErrors}
                hasWritePermissions={this.props.hasWritePermissions}
                retrievedVariableName={retrievedVariable.name}
                className={css`max-width:350px`}
            />
        );
    };

    renderOutputDatastream = (retrievedVariable) => {
        const outputDatastreams = ProcessUtils.getRetrievedVariableOutputDatastreams(this.props.process, retrievedVariable.name);
        const value = outputDatastreams.map(v => {
            return Constants.convertValueToSelectValue(v);
        });

        return (
            <RIEComboBox
                value={value}
                change={this.onOutputDatastreamChange}
                fieldPath={[]}
                onChangeData={{ retrievedVariable: retrievedVariable.name }}
                isDisabled={!this.props.hasWritePermissions}
                processId={this.props.process.name}
                options={this.outputDatastreamOptions}
                multi
                clearable
                creatable
                urlCommit
                minWidth="250px"
            />
        );
    };

    renderOutputVariableMapping = (retrievedVariable) => {
        const outputDatastreams = ProcessUtils.getRetrievedVariableOutputDatastreams(this.props.process, retrievedVariable.name);

        if (outputDatastreams.length > 0) {
            return (
                <Flexbox>
                    <OutputVariableMappingField
                        process={this.props.process}
                        processErrors={this.props.processErrors}
                        hasWritePermissions={this.props.hasWritePermissions}
                        retrievedVariableName={retrievedVariable.name}
                        displayLabel={false}
                    />
                </Flexbox>
            );
        } else {
            return (
                <Flexbox className={emptyStyles}>
                    No assigned output datastream
                </Flexbox>
            );
        }
    };

    renderStartOffset = (retrievedVariable) => {
        const inputDatasetName = retrievedVariable.input_dataset;

        return (
            <RIESecondsInput
                value={this.props.process.variable_retrieval.input_datasets[inputDatasetName].offsets[0]}
                change={this.onFieldChange}
                fieldPath={this.getStartOffsetFieldPath(inputDatasetName)}
                isDisabled={!this.props.hasWritePermissions}
                isFullWidth={false}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
            />
        );
    };

    renderEndOffset = (retrievedVariable) => {
        const inputDatasetName = retrievedVariable.input_dataset;

        return (
            <RIESecondsInput
                value={this.props.process.variable_retrieval.input_datasets[inputDatasetName].offsets[1]}
                change={this.onFieldChange}
                fieldPath={this.getEndOffsetFieldPath(inputDatasetName)}
                isDisabled={!this.props.hasWritePermissions}
                isFullWidth={false}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
            />
        );
    };

    renderInputDataset = (retrievedVariable) => {
        const inputDatasetName = retrievedVariable.input_dataset;

        const datastreamsList = ProcessUtils.getInputDatasetDatastreamsLists(this.props.process, inputDatasetName);
        let datastreamsListComponent = datastreamsList.visibleList;
        if (datastreamsList.tooltip) {
            datastreamsListComponent = <Tooltip placement={'top'} title={datastreamsList.fullList}>{datastreamsList.visibleList}</Tooltip>
        }

        return (
            <Flexbox flexDirection={'row'}>
                <InputDatasetNameField
                    process={this.props.process}
                    processErrors={this.props.processErrors}
                    hasWritePermissions={this.props.hasWritePermissions}
                    inputDatasetName={inputDatasetName}
                    isFullWidth={false}
                />
                <span className={datastreamsListStyles}>
                    [{datastreamsListComponent}]
                </span>
            </Flexbox>
        );
    };

    renderInputDatastreamVariable = (retrievedVariable) => {
        const inputDatasetName = retrievedVariable.input_dataset;
        return (
            <MappingsTable
                className={`mappings-table ${mappingTableStyles}`}
                tableClassName="table table-bordered"
                process={this.props.process}
                inputDatasetName={inputDatasetName}
                displayOnlyVariable={retrievedVariable.name}
                hideToolbar
                disableSelection
                hasWritePermissions={this.props.hasWritePermissions}
                processErrors={this.props.processErrors}
            />
        );
    };
    renderVariableDimensions = (retrievedVariable) => {
        return (
            <VariableDimensionsField
                process={this.props.process}
                processErrors={this.props.processErrors}
                hasWritePermissions={this.props.hasWritePermissions}
                retrievedVariableName={retrievedVariable.name}
            />
        );
    };


    getOverrideTableData = (retrievedVariable) => {
        const coordinateSystemName = retrievedVariable.coordinate_system;
        const dimensions = {};

        retrievedVariable.dims.forEach(dimensionName => {
            const variableOverrideValue = ProcessUtils.getVariableTransformationTypeOverride(this.props.process, retrievedVariable.name, dimensionName);
            dimensions[dimensionName] = variableOverrideValue;
        });

        return { [coordinateSystemName]: dimensions };
    };

    onOverrideChange = (fieldPath, value, onChangeData) => {
        const editor = ProcessUtils.getEditor(this.props.process);

        editor.updateTransformOverrideValueForVariables('transformation_type', [onChangeData.retrievedVariableName], onChangeData.coordinateSystemName, onChangeData.dimensionName, value);
        editor.commit();
    };

    renderAdvancedTransformation = (retrievedVariable) => {
        return (
            <RetrievedVariableTransformation
                process={this.props.process}
                processErrors={this.props.processErrors}
                retrievedVariableNames={[retrievedVariable.name]}
                isOverrideTableVisible
                onOverrideChange={this.onOverrideChange}
                overrideTableData={this.getOverrideTableData(retrievedVariable)}
                spreadsheetView
                overrideTableClassName={transformationTableStyles}
            />
        );

    };

    renderToolbar() {
        return (
            <EntityHeader
                title={'Retrieved Variables'}
                count={this.rowData.length}
                selectedItems={this.state.selectedItems}
            >
                <Toolbar className={css`flex-grow: 1;`}>
                    <OverviewViewDropdown
                        mode={this.props.mode}
                        processId={this.props.processId}
                        revisionNum={this.props.revisionNum}
                        style={{ flexGrow: 1 }}
                    />
                    <ToolbarItem
                        title="Edit Variable Attributes"
                        className="fa fa-pencil yellow"
                        clickHandler={this.props.onEditRetrievedVariables}
                        selectionDependent
                        enabled={this.props.hasWritePermissions}
                    />
                    <ToolbarItem
                        title="Delete"
                        className="fa fa-times delete"
                        clickHandler={this.props.onDeleteRetrievedVariables}
                        selectionDependent
                        enabled={this.props.hasWritePermissions}
                    />
                    <ToolbarItem
                        title="Add Variables"
                        className="fa fa-plus new"
                        clickHandler={ProcessUtils.newRetrievedVariableFunction(this.props.process)}
                        enabled={this.props.hasWritePermissions}
                    />
                </Toolbar>
            </EntityHeader>
        );
    }

    renderHeaderCell = (name, message) => {
        return (
            <Flexbox flexDirection={'row'}>
                <InfoIcon
                    message={message}
                />
                {name}
            </Flexbox>
        );
    };

    getTableColumns() {
        return [
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Variable',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.retrievedVariableName)}
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderRetrievedVariable(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Data Type'
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderDataType(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Units',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.retrievedVariableUnits)}
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderUnits(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Variable Required',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.retrievedVariableRequired)}
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderVariableRequired(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'QC',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.retrievedVariableQCVariable)}
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderQC(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'QC Required',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.retrievedVariableQCRequired)}
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderQCRequired(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Retrieval Rule Set'
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderInputDataset(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Input Datastream Variable'
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderInputDatastreamVariable(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Start Offset',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.inputDatasetOffsetStart)}
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderStartOffset(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'End Offset',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.inputDatasetOffsetEnd)}
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderEndOffset(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Coordinate System',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.coordinateSystem)}
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderCoordinateSystem(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Variable Dimensions'
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderVariableDimensions(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Transformation Method',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.transformationType); }
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderAdvancedTransformation(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Output Datastream',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.outputDatastream); }
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderOutputDatastream(retrievedVariable); }
                    ]
                }
            },
            {
                property: 'retrievedVariable',
                header: {
                    label: 'Output Datastream Variable',
                    formatters: [
                        (name, message) => { return this.renderHeaderCell(name, InfoMessages.outputMapping); }
                    ]
                },
                cell: {
                    formatters: [
                        retrievedVariable => { return this.renderOutputVariableMapping(retrievedVariable); }
                    ]
                }
            }
        ];
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/RetrievedVariablesTable.js